import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings, Building2, Percent, Clock, Bell, Upload, Trash2, Image, Monitor, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Configuracion = () => {
    const { empresa, isSuperAdmin, refreshUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [empresaData, setEmpresaData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingSidebar, setUploadingSidebar] = useState(false);
    const [uploadingLogin, setUploadingLogin] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);
    const fileInputSidebarRef = useRef(null);
    const fileInputLoginRef = useRef(null);
    const fileInputFaviconRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const configRes = await axios.get(`${API_URL}/api/configuracion`);
            setConfig(configRes.data);
            
            if (empresa) {
                setEmpresaData({
                    razon_social: empresa.razon_social,
                    rut: empresa.rut,
                    direccion: empresa.direccion || '',
                    telefono: empresa.telefono || '',
                    email: empresa.email || '',
                    iva_porcentaje: empresa.iva_porcentaje || 19,
                    dias_mora_alerta: empresa.dias_mora_alerta || 30,
                    dia_envio_edp: empresa.dia_envio_edp || 25,
                    dias_espera_respuesta_edp: empresa.dias_espera_respuesta_edp || 5,
                    dias_recordatorio_pago: empresa.dias_recordatorio_pago || '7,3,1,0,-3',
                    incluir_email_como_bcc: empresa.incluir_email_como_bcc !== false,
                    nombre_legal: empresa.nombre_legal || '',
                    banco: empresa.banco || '',
                    cuenta_corriente: empresa.cuenta_corriente || '',
                    rut_pagos: empresa.rut_pagos || '',
                    email_pagos: empresa.email_pagos || ''
                });
            }
        } catch (error) {
            console.error('Error fetching config:', error);
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEmpresa = async (e) => {
        e.preventDefault();
        if (!empresa) return;
        
        try {
            setSaving(true);
            await axios.put(`${API_URL}/api/empresas/${empresa.id}`, empresaData);
            toast.success('Configuración de empresa guardada');
            refreshUser();
        } catch (error) {
            console.error('Error saving empresa:', error);
            toast.error('Error al guardar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveGlobal = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await axios.put(`${API_URL}/api/configuracion`, {
                nombre_plataforma: config.nombre_plataforma,
                iva_default: parseFloat(config.iva_default),
                dias_alerta_vencimiento: parseInt(config.dias_alerta_vencimiento),
                dias_mora_default: parseInt(config.dias_mora_default)
            });
            toast.success('Configuración global guardada');
        } catch (error) {
            console.error('Error saving config:', error);
            toast.error('Error al guardar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Por favor seleccione una imagen');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La imagen no debe superar los 5MB');
            return;
        }

        const setUploading = type === 'sidebar' ? setUploadingSidebar : setUploadingLogin;
        const endpoint = type === 'sidebar' ? '/api/configuracion/logo' : '/api/configuracion/logo-login';
        const fieldName = type === 'sidebar' ? 'logo_plataforma_url' : 'logo_login_url';

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API_URL}${endpoint}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setConfig({
                ...config,
                [fieldName]: response.data.logo_url
            });
            toast.success(type === 'sidebar' ? 'Logo del menú actualizado' : 'Logo del login actualizado');
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(error.response?.data?.detail || 'Error al subir el logo');
        } finally {
            setUploading(false);
            if (type === 'sidebar' && fileInputSidebarRef.current) {
                fileInputSidebarRef.current.value = '';
            }
            if (type === 'login' && fileInputLoginRef.current) {
                fileInputLoginRef.current.value = '';
            }
        }
    };

    const handleFaviconUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type - only PNG
        if (file.type !== 'image/png') {
            toast.error('Por favor seleccione una imagen PNG');
            return;
        }

        // Validate file size (max 1MB for favicon)
        if (file.size > 1 * 1024 * 1024) {
            toast.error('El favicon no debe superar 1MB');
            return;
        }

        try {
            setUploadingFavicon(true);
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API_URL}/api/configuracion/favicon`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setConfig({
                ...config,
                favicon_url: response.data.favicon_url
            });
            
            // Update the favicon in the browser
            updateFavicon(response.data.favicon_url);
            toast.success('Favicon actualizado');
        } catch (error) {
            console.error('Error uploading favicon:', error);
            toast.error(error.response?.data?.detail || 'Error al subir el favicon');
        } finally {
            setUploadingFavicon(false);
            if (fileInputFaviconRef.current) {
                fileInputFaviconRef.current.value = '';
            }
        }
    };

    const handleDeleteFavicon = async () => {
        if (!window.confirm('¿Está seguro de eliminar el favicon?')) return;

        try {
            setUploadingFavicon(true);
            await axios.delete(`${API_URL}/api/configuracion/favicon`);
            setConfig({
                ...config,
                favicon_url: null
            });
            // Reset favicon to default
            updateFavicon('/favicon.ico');
            toast.success('Favicon eliminado correctamente');
        } catch (error) {
            console.error('Error deleting favicon:', error);
            toast.error('Error al eliminar el favicon');
        } finally {
            setUploadingFavicon(false);
        }
    };

    const updateFavicon = (url) => {
        const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
        link.type = 'image/png';
        link.rel = 'icon';
        link.href = url.startsWith('/api') ? `${API_URL}${url}` : url;
        document.getElementsByTagName('head')[0].appendChild(link);
    };

    const handleDeleteLogo = async (type) => {
        if (!window.confirm('¿Está seguro de eliminar el logo?')) return;

        const setUploading = type === 'sidebar' ? setUploadingSidebar : setUploadingLogin;
        const endpoint = type === 'sidebar' ? '/api/configuracion/logo' : '/api/configuracion/logo-login';
        const fieldName = type === 'sidebar' ? 'logo_plataforma_url' : 'logo_login_url';

        try {
            setUploading(true);
            await axios.delete(`${API_URL}${endpoint}`);
            setConfig({
                ...config,
                [fieldName]: null
            });
            toast.success('Logo eliminado correctamente');
        } catch (error) {
            console.error('Error deleting logo:', error);
            toast.error('Error al eliminar el logo');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Ajustes del sistema y empresa
                </p>
            </div>

            {/* Empresa Config */}
            {empresa && empresaData && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Building2 size={18} />
                            Configuración de Empresa
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSaveEmpresa}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Razón Social</Label>
                                    <Input
                                        value={empresaData.razon_social}
                                        onChange={(e) => setEmpresaData({...empresaData, razon_social: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>RUT</Label>
                                    <Input
                                        value={empresaData.rut}
                                        disabled
                                        className="bg-slate-50"
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label>Dirección</Label>
                                    <Input
                                        value={empresaData.direccion}
                                        onChange={(e) => setEmpresaData({...empresaData, direccion: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Teléfono</Label>
                                    <Input
                                        value={empresaData.telefono}
                                        onChange={(e) => setEmpresaData({...empresaData, telefono: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={empresaData.email}
                                        onChange={(e) => setEmpresaData({...empresaData, email: e.target.value})}
                                    />
                                    <label className="flex items-start gap-2 pt-1 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            data-testid="checkbox-incluir-email-bcc"
                                            checked={empresaData.incluir_email_como_bcc !== false}
                                            onChange={(e) => setEmpresaData({...empresaData, incluir_email_como_bcc: e.target.checked})}
                                            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                                        />
                                        <span className="text-xs text-slate-600 group-hover:text-slate-800">
                                            Incorporar este correo como <strong>CCO (con copia oculta)</strong> en los envíos de EDP y gestión de cobranza.
                                        </span>
                                    </label>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Percent size={14} />
                                        IVA (%)
                                    </Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={empresaData.iva_porcentaje}
                                        onChange={(e) => setEmpresaData({...empresaData, iva_porcentaje: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Clock size={14} />
                                        Días para alerta de mora
                                    </Label>
                                    <Input
                                        type="number"
                                        value={empresaData.dias_mora_alerta}
                                        onChange={(e) => setEmpresaData({...empresaData, dias_mora_alerta: parseInt(e.target.value)})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Bell size={14} />
                                        Día de envío de EDP
                                    </Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={empresaData.dia_envio_edp}
                                        onChange={(e) => setEmpresaData({...empresaData, dia_envio_edp: parseInt(e.target.value)})}
                                        data-testid="input-dia-edp"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Día del mes en que se debe enviar el EDP a los clientes (1-31)
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Clock size={14} />
                                        Días máx. espera respuesta EDP
                                    </Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="30"
                                        value={empresaData.dias_espera_respuesta_edp}
                                        onChange={(e) => setEmpresaData({...empresaData, dias_espera_respuesta_edp: parseInt(e.target.value)})}
                                        data-testid="input-dias-espera-edp"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Días máximos de espera para recibir aprobación/rechazo del EDP
                                    </p>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label className="flex items-center gap-2">
                                        <Mail size={14} />
                                        Días para Recordatorios de Pago
                                    </Label>
                                    <Input
                                        type="text"
                                        value={empresaData.dias_recordatorio_pago}
                                        onChange={(e) => setEmpresaData({...empresaData, dias_recordatorio_pago: e.target.value})}
                                        placeholder="7,3,1,0,-3"
                                        data-testid="input-dias-recordatorio"
                                    />
                                    <p className="text-xs text-slate-500">
                                        Lista separada por coma de días respecto al vencimiento en que aparece el botón "Enviar Recordatorio" en el panel.
                                        <br />
                                        <strong>Positivos</strong> = días antes del vencimiento. <strong>0</strong> = el mismo día. <strong>Negativos</strong> = días después (vencido).
                                        <br />Ejemplo: <code className="bg-slate-100 px-1 rounded">7,3,1,0,-3</code> → 7 días antes, 3 antes, 1 antes, el día, y 3 días vencido.
                                    </p>
                                </div>
                            </div>

                            {/* Información para Pagos (EDP) */}
                            <div className="mt-6 pt-6 border-t border-slate-200">
                                <h3 className="font-semibold text-slate-800 mb-1">Información para Pagos (aparece en el EDP)</h3>
                                <p className="text-xs text-slate-500 mb-4">Datos bancarios que se imprimen al pie del PDF del Estado de Pago para que el cliente sepa dónde transferir.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label>Nombre Legal Completo</Label>
                                        <Input
                                            value={empresaData.nombre_legal}
                                            onChange={(e) => setEmpresaData({...empresaData, nombre_legal: e.target.value})}
                                            placeholder="Ej: LEGAV SERVICIOS INTEGRALES LIMITADA"
                                            data-testid="input-config-nombre-legal"
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label>RUT (para datos de transferencia)</Label>
                                        <Input
                                            value={empresaData.rut_pagos}
                                            onChange={(e) => setEmpresaData({...empresaData, rut_pagos: e.target.value})}
                                            placeholder="Si difiere del RUT principal. Si vacío, se usa el RUT de la empresa."
                                            data-testid="input-config-rut-pagos"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Banco</Label>
                                        <Input
                                            value={empresaData.banco}
                                            onChange={(e) => setEmpresaData({...empresaData, banco: e.target.value})}
                                            placeholder="Ej: Banco Estado"
                                            data-testid="input-config-banco"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>N° Cuenta Corriente</Label>
                                        <Input
                                            value={empresaData.cuenta_corriente}
                                            onChange={(e) => setEmpresaData({...empresaData, cuenta_corriente: e.target.value})}
                                            placeholder="Ej: 123456789"
                                            data-testid="input-config-cuenta"
                                        />
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label>Email para envío de comprobantes de pago</Label>
                                        <Input
                                            type="email"
                                            value={empresaData.email_pagos}
                                            onChange={(e) => setEmpresaData({...empresaData, email_pagos: e.target.value})}
                                            placeholder="pagos@empresa.cl"
                                            data-testid="input-config-email-pagos"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <Button type="submit" disabled={saving}>
                                    {saving ? 'Guardando...' : 'Guardar cambios'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Global Config (Superadmin only) */}
            {isSuperAdmin() && config && (
                <>
                    {/* Logos Upload Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Image size={18} />
                                Logos de la Plataforma
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Logo Sidebar/Menu */}
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-medium text-slate-900 flex items-center gap-2">
                                            <Image size={16} />
                                            Logo del Menú (Sidebar)
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Se muestra en el menú lateral de la aplicación.
                                        </p>
                                    </div>
                                    
                                    {/* Preview */}
                                    <div className="w-full h-24 bg-white border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center overflow-hidden">
                                        {config.logo_plataforma_url ? (
                                            <img 
                                                src={`${API_URL}${config.logo_plataforma_url}`}
                                                alt="Logo del menú"
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        ) : (
                                            <div className="text-center text-slate-400">
                                                <Image size={32} className="mx-auto mb-1" />
                                                <span className="text-xs">Sin logo</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        <input
                                            ref={fileInputSidebarRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/gif,image/webp"
                                            onChange={(e) => handleLogoUpload(e, 'sidebar')}
                                            className="hidden"
                                            id="logo-sidebar-upload"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => fileInputSidebarRef.current?.click()}
                                            disabled={uploadingSidebar}
                                            data-testid="upload-logo-sidebar-btn"
                                        >
                                            <Upload size={14} className="mr-2" />
                                            {uploadingSidebar ? 'Subiendo...' : 'Subir'}
                                        </Button>
                                        
                                        {config.logo_plataforma_url && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteLogo('sidebar')}
                                                disabled={uploadingSidebar}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 size={14} className="mr-2" />
                                                Eliminar
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Logo Login */}
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-medium text-slate-900 flex items-center gap-2">
                                            <Monitor size={16} />
                                            Logo del Login
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Se muestra en la pantalla de inicio de sesión.
                                        </p>
                                    </div>
                                    
                                    {/* Preview */}
                                    <div className="w-full h-24 bg-slate-800 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center overflow-hidden">
                                        {config.logo_login_url ? (
                                            <img 
                                                src={`${API_URL}${config.logo_login_url}`}
                                                alt="Logo del login"
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        ) : (
                                            <div className="text-center text-slate-400">
                                                <Monitor size={32} className="mx-auto mb-1" />
                                                <span className="text-xs">Sin logo</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        <input
                                            ref={fileInputLoginRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/gif,image/webp"
                                            onChange={(e) => handleLogoUpload(e, 'login')}
                                            className="hidden"
                                            id="logo-login-upload"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => fileInputLoginRef.current?.click()}
                                            disabled={uploadingLogin}
                                            data-testid="upload-logo-login-btn"
                                        >
                                            <Upload size={14} className="mr-2" />
                                            {uploadingLogin ? 'Subiendo...' : 'Subir'}
                                        </Button>
                                        
                                        {config.logo_login_url && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteLogo('login')}
                                                disabled={uploadingLogin}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 size={14} className="mr-2" />
                                                Eliminar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Favicon */}
                            <div className="mt-8 pt-6 border-t border-slate-200">
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-medium text-slate-900 flex items-center gap-2">
                                            <Image size={16} />
                                            Favicon de la Plataforma
                                        </h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            El ícono que aparece en la pestaña del navegador. Solo PNG, máximo 1MB.
                                        </p>
                                    </div>
                                    
                                    <div className="flex items-center gap-6">
                                        {/* Preview */}
                                        <div className="w-16 h-16 bg-white border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center overflow-hidden">
                                            {config.favicon_url ? (
                                                <img 
                                                    src={`${API_URL}${config.favicon_url}`}
                                                    alt="Favicon"
                                                    className="w-8 h-8 object-contain"
                                                />
                                            ) : (
                                                <div className="text-center text-slate-400">
                                                    <Image size={20} className="mx-auto" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <input
                                                ref={fileInputFaviconRef}
                                                type="file"
                                                accept="image/png"
                                                onChange={handleFaviconUpload}
                                                className="hidden"
                                                id="favicon-upload"
                                            />
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={() => fileInputFaviconRef.current?.click()}
                                                disabled={uploadingFavicon}
                                                data-testid="upload-favicon-btn"
                                            >
                                                <Upload size={14} className="mr-2" />
                                                {uploadingFavicon ? 'Subiendo...' : 'Subir Favicon'}
                                            </Button>
                                            
                                            {config.favicon_url && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleDeleteFavicon}
                                                    disabled={uploadingFavicon}
                                                    className="text-red-600 hover:text-red-700"
                                                >
                                                    <Trash2 size={14} className="mr-2" />
                                                    Eliminar
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <p className="text-xs text-slate-400 mt-6">
                                Formatos permitidos para logos: PNG, JPG, GIF, WEBP (máx 5MB). Favicon: solo PNG (máx 1MB).
                            </p>
                        </CardContent>
                    </Card>

                    {/* Other Global Settings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Settings size={18} />
                                Configuración Global de Plataforma
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSaveGlobal}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nombre de la Plataforma</Label>
                                        <Input
                                            value={config.nombre_plataforma}
                                            onChange={(e) => setConfig({...config, nombre_plataforma: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Percent size={14} />
                                            IVA Default (%)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={config.iva_default}
                                            onChange={(e) => setConfig({...config, iva_default: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Bell size={14} />
                                            Días alerta vencimiento
                                        </Label>
                                        <Input
                                            type="number"
                                            value={config.dias_alerta_vencimiento}
                                            onChange={(e) => setConfig({...config, dias_alerta_vencimiento: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2">
                                            <Clock size={14} />
                                            Días mora default
                                        </Label>
                                        <Input
                                            type="number"
                                            value={config.dias_mora_default}
                                            onChange={(e) => setConfig({...config, dias_mora_default: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <Button type="submit" disabled={saving}>
                                        {saving ? 'Guardando...' : 'Guardar configuración global'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default Configuracion;
